import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Info } from '@/src/components/ui/icons/info'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/src/components/ui/tooltip'
import {
  fromWalletAtom,
  isTransferringFromAutopilotToDestinationAtom,
  isUserRejectionErrorAtom,
  routeAtom,
} from '@/src/stores/route-machine/atoms'
import { tradeStepAtom } from '@/src/stores/trade/atoms'
import { WalletIcon } from '../accounts/wallet-icon'
import { StepRemainingTimeText } from '../route-details/step-remaining-time-text'
import TokenImg from '../ui/token-img'
import { BasicStep, BasicStepTitle } from './basic-step'

const Title = () => {
  const tradeStep = useAtomValue(tradeStepAtom)
  const isUserRejectionError = useAtomValue(isUserRejectionErrorAtom)
  const title = useMemo(() => {
    if (isUserRejectionError) return 'Approval rejected'
    if (tradeStep.isUserSigning)
      return `Approve ${tradeStep.currentTradeStepDetails?.from_token.ticker}`
    return 'Trade in progress'
  }, [
    isUserRejectionError,
    tradeStep.isUserSigning,
    tradeStep.currentTradeStepDetails,
  ])

  return (
    <div className='flex items-center gap-1 w-full'>
      <BasicStepTitle title={title} isError={tradeStep.isError} />
      {tradeStep.isUserSigning && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info
                width={16}
                height={16}
                className='text-tertiary min-w-4'
                strokeWidth={1.5}
              />
            </TooltipTrigger>
            <TooltipContent
              className='max-w-[280px] bg-surface1-100'
              side='right'
              hideArrow
            >
              <p className='text-secondary text-xs'>
                Token approvals are a feature of Ethereum networks that require
                a user to permit a third party permission to spend a designated
                number of tokens on their behalf.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}

export const TradeStep = () => {
  const route = useAtomValue(routeAtom)
  const tradeStep = useAtomValue(tradeStepAtom)
  const isUserRejectionError = useAtomValue(isUserRejectionErrorAtom)
  const isTransferringFromHotkeyToDestination = useAtomValue(
    isTransferringFromAutopilotToDestinationAtom,
  )
  const fromWalletDetails = useAtomValue(fromWalletAtom)
  const isWarning = tradeStep.error && isUserRejectionError

  const isWaiting = tradeStep.isUserSigning
  const waitingStart = useRef(Date.now())
  const prevIsWaiting = useRef(isWaiting)
  const [extraTime, setExtraTime] = useState(0)

  useEffect(() => {
    if (isWaiting && !prevIsWaiting.current) {
      waitingStart.current = Date.now()
    }
    if (!isWaiting && prevIsWaiting.current) {
      const waitingDuration = Date.now() - waitingStart.current
      setExtraTime(extraTime + waitingDuration)
    }
    prevIsWaiting.current = isWaiting
  }, [extraTime, isWaiting])
  const transferDetailsWithExtraTime = useMemo(() => {
    if (tradeStep.transferDetails) {
      return {
        ...tradeStep.transferDetails,
        estimatedTimeMs: tradeStep.transferDetails.estimatedTimeMs + extraTime,
      }
    }
    return undefined
  }, [extraTime, tradeStep.transferDetails])

  return (
    <BasicStep
      title={<Title />}
      isActive={tradeStep.isActive}
      isLoading={tradeStep.isLoading}
      isError={tradeStep.isError}
      isWarning={isWarning}
      isWaiting={isWaiting}
      isSuccess={tradeStep.isSuccess}
      transferDetails={transferDetailsWithExtraTime}
    >
      <div className='flex flex-col text-left gap-2'>
        <h6 className='flex items-center gap-2 font-semibold text-primary text-xl'>
          {tradeStep.isUserSigning ? (
            <>
              <span>Approve in</span>
              {!!fromWalletDetails?.logoUrl && (
                <WalletIcon
                  logoUrl={fromWalletDetails.logoUrl}
                  walletName={fromWalletDetails.displayName}
                  size={24}
                  className='size-6'
                />
              )}
              <span>{fromWalletDetails?.displayName}</span>
            </>
          ) : (
            <>
              <TokenImg
                src={route?.InputAssetMetadata.logo_img_url}
                ticker={route?.InputAssetMetadata.ticker}
                width={24}
                height={24}
                className='size-6'
                fallbackContainerClassName='size-6'
                unoptimized
              />
              <span>{route?.InputAssetMetadata?.ticker}</span>
              <span className='text-tertiary font-normal'>to</span>
              <TokenImg
                src={route?.OutputAssetMetadata.logo_img_url}
                ticker={route?.OutputAssetMetadata.ticker}
                width={24}
                height={24}
                className='size-6'
                fallbackContainerClassName='size-6'
                unoptimized
              />
              <span>{route?.OutputAssetMetadata?.ticker}</span>
            </>
          )}
        </h6>
        {!isWarning ? (
          <>
            {transferDetailsWithExtraTime ? (
              <StepRemainingTimeText
                {...transferDetailsWithExtraTime}
                paused={isWaiting}
              />
            ) : null}
            {isTransferringFromHotkeyToDestination ? (
              <p className='text-secondary text-sm'>
                Transferring funds to destination wallet
              </p>
            ) : null}
          </>
        ) : (
          <span className='text-secondary text-sm'>
            Retry in your wallet or cancel this trade
          </span>
        )}
      </div>
    </BasicStep>
  )
}
